/**
 * One item on the count sheet: what it is, what has been counted, and whether
 * that has reached Business Central.
 *
 * **The blank/zero distinction is two visual channels, never one** (§ 3, S3, and
 * CLAUDE.md rule 5):
 *
 * - **Blank** — an empty field and a muted "not counted" label. There is
 *   **no placeholder in the box**, because a greyed-out number in a quantity field
 *   is exactly the thing a manager can read as a value, and half of rule 5 is that
 *   nobody should ever have to wonder whether they typed it.
 * - **Zero** — a bold `0` in the field *and* a distinct "counted: 0" chip beside
 *   it. Two channels, so it survives a freezer, a glare, and a reading-glasses
 *   situation.
 *
 * **No on-hand, no cost, before the line is saved.** Two amendments to D-1, both
 * display only and both from separate server reads: Cold Link's on-hand beside the
 * box (Copeland 2026-10-29, arrives as `onHandView`), and the blind-entry reveal at
 * the other five areas (Copeland 2026-10-30, arrives as the `reveal` slot — only
 * after the line has saved, and the server refuses it before then). The count
 * payload itself has no on-hand in it, and `unit_cost` — which it does carry — is
 * deliberately not rendered.
 */
import type { ReactNode } from "react";

import type { CountLine, CountListItem } from "../api/types";
import { defaultUnit } from "./defaultUnit";
import type { CountState, SyncState } from "./entryModel";
import type { OnHandRowView } from "./onHandView";

export interface ItemRowProps {
  item: CountListItem;
  staged: CountLine | undefined;
  state: CountState;
  /** The typed string, when the manager has touched this field this session. */
  draft: string | undefined;
  sync: SyncState;
  /** The server's own words when a sync was refused, or a local validation note. */
  message: string | undefined;
  editable: boolean;
  /**
   * The unit this row is being counted in (item 3). Null means the item's base
   * unit, which is what every row defaults to and what most rows can only be.
   */
  unit?: string | null;
  /**
   * BC on-hand beside the box — **Cold Link only** (Copeland 2026-10-29). Absent
   * everywhere else, which is what keeps D-1 structural. Never a value for the
   * box: the entry field above is not fed from this.
   */
  onHandView?: OnHandRowView | null;
  /**
   * The post-save reveal (PHASE_blind-entry-reveal.md) — a self-contained cell that
   * asks the server only once this line has saved. Absent at Cold Link.
   */
  reveal?: ReactNode;
  /** "Open receipt: 6 BAG on PO1001" — a document's quantity, never stock. */
  pendingMarker?: string | null;
  onChange: (text: string) => void;
  onCommit: () => void;
  onUnitChange?: (code: string) => void;
  onOpenSheet: () => void;
}

const SYNC_LABEL: Record<SyncState, string> = {
  clean: "",
  pending: "saving…",
  synced: "saved",
  refused: "not saved",
};

export function ItemRow({
  item,
  staged,
  state,
  draft,
  sync,
  message,
  editable,
  unit,
  onHandView,
  reveal,
  pendingMarker,
  onChange,
  onCommit,
  onUnitChange,
  onOpenSheet,
}: ItemRowProps) {
  // The field shows what was typed if anything was, otherwise what BC holds,
  // otherwise nothing at all. `??` and not `||`: a staged "0" must survive, and
  // `""` from a draft is a real state (the manager emptied the box) that must not
  // fall through to the staged value while they are still typing.
  const value = draft ?? staged?.counted_qty ?? "";
  const inputId = `qty-${item.item_no}`;

  return (
    <li className={`item-row item-row--${state}`} data-testid={`row-${item.item_no}`}>
      <div className="item-row__identity">
        {/* The whole identity is the sheet trigger — a 44px-plus target, and it is
            a button so it is reachable without a pointer. */}
        <button
          type="button"
          className="item-row__open"
          onClick={onOpenSheet}
          data-testid={`open-${item.item_no}`}
        >
          <span className="item-row__no">{item.item_no}</span>
          <span className="item-row__desc">{item.description ?? "—"}</span>
        </button>
        <div className="item-row__marks">
          {/* Informational only. One total per item, lot-tracked items included. */}
          {item.lot_tracked === true && (
            <span className="chip chip--lot" data-testid={`lot-${item.item_no}`}>
              lot-tracked
            </span>
          )}
          {item.lot_tracked == null && <span className="chip chip--unknown">lot tracking unknown</span>}
          {item.blocked === true && <span className="chip chip--blocked">blocked in BC</span>}
        </div>
      </div>

      <div className="item-row__entry">
        <label className="visually-hidden" htmlFor={inputId}>
          Counted quantity for {item.item_no}
        </label>
        <input
          id={inputId}
          className={`qty qty--${state}`}
          // `decimal` gives an iPad a numeric keypad with a decimal point and no
          // return-key surprises. `type="text"` and not `number`: a number input
          // silently drops what it cannot parse, reorders nothing, and would let
          // the browser decide what a quantity is. The typed string is the value.
          type="text"
          inputMode="decimal"
          autoComplete="off"
          enterKeyHint="done"
          value={value}
          disabled={!editable}
          aria-describedby={message ? `${inputId}-msg` : undefined}
          onChange={(event) => onChange(event.target.value)}
          onBlur={onCommit}
          onKeyDown={(event) => {
            if (event.key === "Enter") {
              event.preventDefault();
              (event.target as HTMLInputElement).blur();
            }
          }}
          data-testid={`qty-${item.item_no}`}
        />
        {/*
          Item 3. A select appears ONLY where Business Central holds a purchase
          unit that differs from base — live that is 37 of Ybor Wholesale's 112
          items and none at all of St. Pete's 243, so the great majority of rows
          render exactly the static label they always did.

          The chosen code is all that leaves this screen. No quantity is ever
          converted here: what syncs is the manager's own figure plus this code,
          and Business Central does the conversion at posting.

          A still-blank line opens on the PURCHASE unit (RULED 2026-09-24,
          `PHASE_purchase-uom-default.md`) — FL Ave buys and counts ING0001N by
          the box, and base-first put the extra tap on exactly the lines where it
          matters most. `unit` already carries a draft's or staged line's own
          choice, so the default below is reached only on a line nobody has
          counted yet.
        */}
        {(item.unit_options?.length ?? 0) > 1 && onUnitChange ? (
          <select
            className="item-row__uom item-row__uom--select"
            value={unit ?? defaultUnit(item) ?? ""}
            disabled={!editable}
            aria-label={`Unit for ${item.item_no}`}
            onChange={(event) => onUnitChange(event.target.value)}
            data-testid={`uom-${item.item_no}`}
          >
            {item.unit_options?.map((option) => (
              <option key={option.code} value={option.code}>
                {option.code}
                {option.is_base || option.qty_per_base === null
                  ? ""
                  : ` (${option.qty_per_base} ${item.base_unit_of_measure ?? ""})`}
              </option>
            ))}
          </select>
        ) : (
          <span className="item-row__uom">{item.base_unit_of_measure ?? ""}</span>
        )}
      </div>

      {onHandView && (
        <div className="item-row__bc" data-testid={`bc-${item.item_no}`}>
          <span className="muted small" data-testid={`bc-onhand-${item.item_no}`}>
            {onHandView.onHandLabel}
          </span>
          {onHandView.differenceLabel && (
            <span
              className={onHandView.belowBc ? "chip chip--warn" : "chip"}
              data-testid={`bc-diff-${item.item_no}`}
              role={onHandView.belowBc ? "status" : undefined}
            >
              {onHandView.belowBc ? "below BC: " : ""}
              {onHandView.differenceLabel}
            </span>
          )}
        </div>
      )}

      {pendingMarker && (
        <div className="item-row__pending" data-testid={`pending-${item.item_no}`}>
          <span className="chip chip--warn">{pendingMarker}</span>
        </div>
      )}

      {reveal}

      <div className="item-row__status">
        {state === "blank" && (
          <span className="muted small" data-testid={`blank-${item.item_no}`}>
            not counted
          </span>
        )}
        {state === "zero" && (
          <span className="chip chip--zero" data-testid={`zero-${item.item_no}`}>
            counted: 0
          </span>
        )}
        {state === "counted" && (
          <span className="chip chip--counted" data-testid={`counted-${item.item_no}`}>
            counted
          </span>
        )}
        {sync !== "clean" && (
          <span className={`sync sync--${sync}`} data-testid={`sync-${item.item_no}`}>
            {SYNC_LABEL[sync]}
          </span>
        )}
      </div>

      {message && (
        <p
          className={sync === "refused" ? "error-text small" : "muted small"}
          id={`${inputId}-msg`}
          data-testid={`msg-${item.item_no}`}
        >
          {message}
        </p>
      )}
    </li>
  );
}
